import { FC, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { CloudDownload } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { IRoleCategory } from '../../../models';
import { useSnackbar } from 'notistack';

interface IDownloadCSV {
  roleName: string;
  roleCategories: IRoleCategory[];
}

export const DownloadCSV: FC<IDownloadCSV> = ({ roleName, roleCategories }) => {
  const [isDownloadingCSV, setIsDownloadingCSV] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const recursiveCategories = () => {
    const csv = [];

    // After escaping commas with quotes that wrap fields,
    // quotes within each field also need to be escaped
    const escapeCommasAndQuotes = (str) => {
      return `"${str.replace(/"/g, '""')}"`
    };

    const traverseCategories = (categories, parentCategoryId = '', parentClassName = '') => {
      categories.forEach((category) => {
        const { className, id, parts, subCategories } = category;
        const categoryId = parentCategoryId ? `${parentCategoryId}->${id}` : id.toString();
        const categoryClassName = parentClassName ? `${parentClassName}->${className}` : className;
        parts.forEach((part) => {
          if (part.assigned) {
            csv.push([categoryId, escapeCommasAndQuotes(categoryClassName), part.id, escapeCommasAndQuotes(part.name)]);
          }
        });
        traverseCategories(subCategories, categoryId, categoryClassName);
      });
    };

    traverseCategories(roleCategories);
    return csv;
  };

  return (
    <LoadingButton
      loading={isDownloadingCSV}
      loadingPosition="start"
      color="primary"
      variant="contained"
      disabled={isDownloadingCSV}
      onClick={() => {
        try {
          setIsDownloadingCSV(true);
          const csvArray = recursiveCategories();
          const BOM = String.fromCharCode(0xfeff);
          
          // Initialize CSV with headers
          let csv = 'Category ID,Category Name,Part ID,Part Name\n';
          csvArray.forEach((row) => {
            csv += row.join(',');
            csv += '\n';
          });
          
          // Create a file the user can download
          const el = document.createElement('a');
          // encodeURIComponent must be used instead of encodeURI to escape , / ? : @ & = + $ #
          el.href = `data:text/csv;charset=UTF-8,${BOM}${encodeURIComponent(csv)}`;
          el.target = '_blank';
          el.download = `${roleName.toLowerCase().replace(/ /g, '_')}_role_assignments_${format(parseISO(new Date().toISOString()), 'yyyy_LL_dd_hh_mm_ss')}.csv`;
          // Download the file
          el.click();
        } catch (error) {
          enqueueSnackbar(`Error downloading CSV file: ${JSON.stringify(error)}`, {
            variant: 'error'
          });
        } finally {
          setIsDownloadingCSV(false);
        }
      }}
      startIcon={<CloudDownload />}
    >
      CSV
    </LoadingButton>
  )
};
